import type {
  TiebreakerTeam, TiebreakerResult, TiebreakerStepFn,
  StepContext, StepResult, GameResultsLookup, TiebreakerOptions,
} from './types';
import { RankMethod } from '../enums/rank-method';
import { defaultCoinFlip, addExplanation } from './helpers';

/**
 * Create a fresh StepContext for a tiebreaker run.
 */
export function createContext<T extends TiebreakerTeam>(
  games: GameResultsLookup,
  options?: TiebreakerOptions,
): StepContext<T> {
  return {
    games,
    explanations: new Map(),
    explainerDetails: new Map(),
    rankMethods: new Map(),
    coinFlipFn: (options?.coinFlipFn as (teams: T[]) => T) ?? defaultCoinFlip,
  };
}

/**
 * Run an ordered array of tiebreaker steps.
 *
 * Semantics:
 * - Each step receives the current tied group.
 * - Full break: winners cover all input teams → done.
 * - No break: advance to next step.
 * - Partial break: place winners in order, restart remaining from step 0.
 * - If all steps exhaust without resolution, coin flip the remainder.
 *
 * Returns teams in final order (best first).
 */
export function runTiebreakerSteps<T extends TiebreakerTeam>(
  teams: T[],
  steps: TiebreakerStepFn<T>[],
  ctx: StepContext<T>,
): TiebreakerResult<T> {
  const ordered: T[] = [];
  let remaining = [...teams];
  const maxIterations = teams.length * steps.length + teams.length;
  let iterations = 0;

  while (remaining.length > 0) {
    if (iterations++ > maxIterations) {
      // Safety valve: coin flip everything left
      while (remaining.length > 0) {
        const winner = ctx.coinFlipFn(remaining);
        ctx.rankMethods.set(winner.teamAbbreviation, RankMethod.CoinFlip);
        addExplanation(ctx, winner, 'Cycle protection: coin flip.');
        ordered.push(winner);
        remaining = remaining.filter(t => t !== winner);
      }
      break;
    }

    if (remaining.length === 1) {
      ordered.push(remaining[0]);
      break;
    }

    let resolved = false;
    for (let stepIdx = 0; stepIdx < steps.length; stepIdx++) {
      const result: StepResult<T> = steps[stepIdx](remaining, ctx);

      if (result.winners.length === 0 && result.remaining.length === remaining.length) {
        // No break: advance to next step
        continue;
      }

      if (result.winners.length > 0 && result.remaining.length === 0) {
        // Full break: all teams ordered
        for (const w of result.winners) {
          if (result.rankMethod != null) {
            ctx.rankMethods.set(w.teamAbbreviation, result.rankMethod);
          }
          ordered.push(w);
        }
        remaining = [];
        resolved = true;
        break;
      }

      if (result.winners.length > 0 && result.remaining.length > 0) {
        // Partial break: place winners, restart remaining from step 0
        for (const w of result.winners) {
          if (result.rankMethod != null) {
            ctx.rankMethods.set(w.teamAbbreviation, result.rankMethod);
          }
          ordered.push(w);
        }
        remaining = result.remaining;
        resolved = true;
        break; // break inner loop to restart from step 0
      }
    }

    if (!resolved) {
      // All steps exhausted with no break: coin flip remainder one at a time
      while (remaining.length > 0) {
        const winner = ctx.coinFlipFn(remaining);
        ctx.rankMethods.set(winner.teamAbbreviation, RankMethod.CoinFlip);
        addExplanation(ctx, winner, 'All tiebreaker steps exhausted. Coin flip.');
        ordered.push(winner);
        remaining = remaining.filter(t => t !== winner);
      }
    }
  }

  return {
    orderedTeams: ordered,
    rankMethods: ctx.rankMethods,
    explanations: ctx.explanations,
    explainerDetails: ctx.explainerDetails,
  };
}

/**
 * Convenience: pick a single winner from a tied group using step arrays.
 * Used by NFL/NHL where the tiebreaker produces one winner at a time.
 */
export function runSingleWinnerSteps<T extends TiebreakerTeam>(
  teams: T[],
  steps: TiebreakerStepFn<T>[],
  ctx: StepContext<T>,
): T {
  const result = runTiebreakerSteps(teams, steps, ctx);
  return result.orderedTeams[0];
}
